import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import { PageHeader } from '../../components/layout';
import { KPICard } from '../../components/common';
import readFirstRegisterService from '../../services/api/readFirstRegisterService';
import { RootState } from '../../store';

type ColumnConfig = { key: string; label: string; align?: 'left' | 'right' };
type RegisterConfig = {
  title: string;
  group: 'HSE' | 'Contract Admin';
  endpoint?: string;
  detailEndpoint?: string;
  requiresProject?: boolean;
  columns?: ColumnConfig[];
  dashboardFeeds?: Array<{ label: string; endpoint: string; path: string; statusKey?: string }>;
};

const HSE_COLUMNS: ColumnConfig[] = [
  { key: 'incidentNumber|accidentNumber|jsaNumber|inspectionNumber|standDownNumber|id', label: 'Reference' },
  { key: 'title|subject|assetRef|contactName|description|reason', label: 'Summary' },
  { key: 'severity|riskLevel|result|approvalStatus|status', label: 'Status' },
  { key: 'taskId|teamId|location|projectId', label: 'Scope' },
  { key: 'reportedAt|accidentDate|checkDate|startedAt|createdAt', label: 'Date' },
];

const COMMERCIAL_COLUMNS: ColumnConfig[] = [
  { key: 'contractNumber|subcontractNumber|instructionNumber|rfiNumber|variationNumber|eotNumber|claimNumber|contraNumber|noticeNumber|applicationNumber|id', label: 'Reference' },
  { key: 'title|subject|description', label: 'Summary' },
  { key: 'approvalStatus|status', label: 'Status' },
  { key: 'counterparty|party|contractId|taskId|projectId', label: 'Link' },
  { key: 'contractValue|amount|costDelta|claimedAmount|certifiedAmount|claimableValue|agreedCost', label: 'Value', align: 'right' },
];

const configs: Record<string, RegisterConfig> = {
  '/hse': {
    title: 'HSE Dashboard',
    group: 'HSE',
    requiresProject: true,
    dashboardFeeds: [
      { label: 'Incidents', endpoint: '/incidents', path: '/hse/incidents', statusKey: 'status' },
      { label: 'Accidents', endpoint: '/accidents', path: '/hse/accidents' },
      { label: 'JSAs', endpoint: '/jsas', path: '/hse/jsas', statusKey: 'approvalStatus' },
      { label: 'Hazards', endpoint: '/hazard-assessments', path: '/hse/hazards', statusKey: 'riskLevel' },
      { label: 'PPE Checks', endpoint: '/ppe-checks', path: '/hse/ppe-checks' },
      { label: 'Safety Inspections', endpoint: '/safety-inspections', path: '/hse/safety-inspections', statusKey: 'result' },
      { label: 'Emergency Contacts', endpoint: '/emergency-contacts', path: '/hse/emergency-contacts' },
      { label: 'First Aid', endpoint: '/first-aid-readiness', path: '/hse/first-aid-readiness', statusKey: 'status' },
      { label: 'Stand Downs', endpoint: '/safety-stand-downs', path: '/hse/stand-downs', statusKey: 'status' },
    ],
  },
  '/hse/incidents': { title: 'Incidents', group: 'HSE', endpoint: '/incidents', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/accidents': { title: 'Accident Records', group: 'HSE', endpoint: '/accidents', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/jsas': { title: 'JSAs', group: 'HSE', endpoint: '/jsas', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/hazards': { title: 'Hazard Assessments', group: 'HSE', endpoint: '/hazard-assessments', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/ppe-checks': { title: 'PPE Checks', group: 'HSE', endpoint: '/ppe-checks', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/safety-inspections': { title: 'Safety Inspections', group: 'HSE', endpoint: '/safety-inspections', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/emergency-contacts': { title: 'Emergency Contacts', group: 'HSE', endpoint: '/emergency-contacts', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/first-aid-readiness': { title: 'First-Aid Readiness', group: 'HSE', endpoint: '/first-aid-readiness', requiresProject: true, columns: HSE_COLUMNS },
  '/hse/stand-downs': { title: 'Safety Stand-Downs', group: 'HSE', endpoint: '/safety-stand-downs', requiresProject: true, columns: HSE_COLUMNS },

  '/contract-admin': {
    title: 'Contract Admin Dashboard',
    group: 'Contract Admin',
    requiresProject: true,
    dashboardFeeds: [
      { label: 'Contracts', endpoint: '/contracts', path: '/contract-admin/contracts', statusKey: 'approvalStatus' },
      { label: 'Subcontracts', endpoint: '/subcontracts', path: '/contract-admin/subcontracts', statusKey: 'approvalStatus' },
      { label: 'Instructions', endpoint: '/instructions', path: '/contract-admin/instructions', statusKey: 'approvalStatus' },
      { label: 'RFIs', endpoint: '/rfis', path: '/contract-admin/rfis', statusKey: 'status' },
      { label: 'Variations', endpoint: '/variations', path: '/contract-admin/variations', statusKey: 'status' },
      { label: 'EOTs', endpoint: '/extensions-of-time', path: '/contract-admin/extensions-of-time', statusKey: 'status' },
      { label: 'Claims', endpoint: '/claims', path: '/contract-admin/claims', statusKey: 'approvalStatus' },
      { label: 'Payment Apps', endpoint: '/payment-applications', path: '/contract-admin/payment-applications', statusKey: 'status' },
    ],
  },
  '/contract-admin/contracts': { title: 'Contracts', group: 'Contract Admin', endpoint: '/contracts', detailEndpoint: '/contracts', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/subcontracts': { title: 'Subcontracts', group: 'Contract Admin', endpoint: '/subcontracts', detailEndpoint: '/subcontracts', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/instructions': { title: 'Instructions', group: 'Contract Admin', endpoint: '/instructions', detailEndpoint: '/instructions', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/rfis': { title: 'RFIs', group: 'Contract Admin', endpoint: '/rfis', detailEndpoint: '/rfis', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/variations': { title: 'Variations', group: 'Contract Admin', endpoint: '/variations', detailEndpoint: '/variations', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/extensions-of-time': { title: 'Extensions of Time', group: 'Contract Admin', endpoint: '/extensions-of-time', detailEndpoint: '/extensions-of-time', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/claims': { title: 'Claims', group: 'Contract Admin', endpoint: '/claims', detailEndpoint: '/claims', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/contra-charges': { title: 'Contra-Charges', group: 'Contract Admin', endpoint: '/contra-charges', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/notices': { title: 'Notices', group: 'Contract Admin', endpoint: '/notices', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/payment-applications': { title: 'Payment Applications', group: 'Contract Admin', endpoint: '/payment-applications', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/retention': { title: 'Retention Movements', group: 'Contract Admin', endpoint: '/retention-movements', requiresProject: true, columns: COMMERCIAL_COLUMNS },
  '/contract-admin/final-accounts': { title: 'Final Accounts', group: 'Contract Admin', endpoint: '/final-accounts', requiresProject: true, columns: COMMERCIAL_COLUMNS },
};

const firstValue = (row: any, keyList: string): any => {
  for (const key of keyList.split('|')) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '-';
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(value))) return new Date(value).toLocaleDateString();
  return String(value);
};

const rowId = (row: any, index: number) => row.id || row.uuid || `${index}`;

interface ReadFirstRegisterPageProps {
  configPath?: string;
  embedded?: boolean;
}

export default function ReadFirstRegisterPage({ configPath, embedded = false }: ReadFirstRegisterPageProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const config = configs[configPath || location.pathname] || configs['/hse'];
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [rows, setRows] = useState<any[]>([]);
  const [dashboardRows, setDashboardRows] = useState<Array<{ label: string; rows: any[]; statusKey?: string }>>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(() => (
    config.requiresProject && activeProjectId ? { projectId: activeProjectId } : {}
  ), [activeProjectId, config.requiresProject]);

  const load = async () => {
    if (config.requiresProject && !activeProjectId) {
      setRows([]);
      setDashboardRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (config.dashboardFeeds) {
        const feeds = await Promise.all(config.dashboardFeeds.map(async (feed) => ({
          label: feed.label,
          statusKey: feed.statusKey,
          rows: await readFirstRegisterService.list(feed.endpoint, params),
        })));
        setDashboardRows(feeds);
        setRows([]);
      } else if (config.endpoint) {
        setRows(await readFirstRegisterService.list(config.endpoint, params));
        setDashboardRows([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to load ${config.title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [location.pathname, configPath, activeProjectId]);

  const openDetail = async (row: any) => {
    if (!config.detailEndpoint || !row.id) {
      setSelected(row);
      return;
    }
    try {
      setError(null);
      setSelected(await readFirstRegisterService.get(config.detailEndpoint, row.id));
    } catch {
      setSelected(row);
    }
  };

  const totalRows = config.dashboardFeeds
    ? dashboardRows.reduce((sum, feed) => sum + feed.rows.length, 0)
    : rows.length;

  return (
    <Box>
      {!embedded && (
        <PageHeader
          title={config.title}
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: config.group }, { label: config.title }]}
          actions={<Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>}
        />
      )}
      {embedded && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">{config.title}</Typography>
            <Typography variant="body2" color="text.secondary">{config.group} register</Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        </Stack>
      )}

      {config.requiresProject && !activeProjectId && (
        <Alert severity="info" sx={{ mb: 2 }}>Select a project to load this register.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : config.dashboardFeeds ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard title="Total Rows" value={totalRows} color="primary" />
          </Grid>
          {dashboardRows.map((feed) => {
            const configuredFeed = config.dashboardFeeds?.find((item) => item.label === feed.label);
            const flagged = feed.statusKey
              ? feed.rows.filter((row) => !['closed', 'approved', 'paid', 'adequate', 'pass', 'passed'].includes(String(row[feed.statusKey!] || '').toLowerCase())).length
              : feed.rows.length;
            return (
              <Grid item xs={12} sm={6} md={3} key={feed.label}>
                <KPICard
                  title={feed.label}
                  value={flagged}
                  color={flagged > 0 ? 'warning' : 'success'}
                  onClick={configuredFeed ? () => navigate(configuredFeed.path) : undefined}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {(config.columns || []).map((column) => (
                  <TableCell key={column.label} align={column.align}>{column.label}</TableCell>
                ))}
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={rowId(row, index)} hover>
                  {(config.columns || []).map((column) => {
                    const value = firstValue(row, column.key);
                    const looksStatus = column.label.toLowerCase().includes('status');
                    return (
                      <TableCell key={column.label} align={column.align}>
                        {looksStatus && value !== '-' ? <Chip size="small" label={formatValue(value)} /> : formatValue(value)}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Button size="small" onClick={() => openDetail(row)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(config.columns || []).length + 1}>
                    <Alert severity="info">No rows returned from {config.endpoint}.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <Box sx={{ width: { xs: 360, sm: 520 }, p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">{config.title} Detail</Typography>
              <Typography variant="body2" color="text.secondary">Read-only register view</Typography>
            </Box>
            {selected && Object.entries(selected).map(([key, value]) => (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">{key}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{formatValue(value)}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
